/**
 * Admin API Client
 */

import type {
  ApiKey,
  CreateApiKeyInput,
  CreateApiKeyResponse,
  CostSummary,
  SlowFunction,
  MostCalledFunction,
  MetricsTrend,
  ListApiKeysResponse,
  ListAuditLogsResponse,
  ListMetricsResponse,
  Tenant,
  CreateTenantInput,
  UpdateTenantInput,
  ListTenantsResponse,
  User,
  CreateUserInput,
  UpdateUserInput,
  ListUsersResponse,
  MemoryStatsSummary,
  SystemHealthSummary,
  SystemHealthSnapshot,
  ErrorSummary,
  AlertSummary,
  Alert,
  ErrorLog,
} from './types';

class AdminApiClient {
  private baseUrl: string;
  private masterKey: string | null = null;

  constructor(baseUrl: string = '') {
    this.baseUrl = baseUrl;
  }

  setMasterKey(key: string) {
    this.masterKey = key;
  }

  clearMasterKey() {
    this.masterKey = null;
  }

  getMasterKey(): string | null {
    return this.masterKey;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (this.masterKey) {
      headers['X-API-Key'] = this.masterKey;
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || `HTTP ${response.status}`);
    }

    return data as T;
  }

  // ============================================================================
  // Tenants
  // ============================================================================

  async listTenants(options?: {
    limit?: number;
    offset?: number;
  }): Promise<ListTenantsResponse> {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));

    const query = params.toString();
    return this.request<ListTenantsResponse>(`/admin/tenants${query ? `?${query}` : ''}`);
  }

  async getTenant(uuid: string): Promise<{ success: boolean; tenant: Tenant }> {
    return this.request(`/admin/tenants/${uuid}`);
  }

  async createTenant(input: CreateTenantInput): Promise<{ success: boolean; tenant: Tenant }> {
    return this.request<{ success: boolean; tenant: Tenant }>('/admin/tenants', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async updateTenant(uuid: string, input: UpdateTenantInput): Promise<{ success: boolean; tenant: Tenant }> {
    return this.request<{ success: boolean; tenant: Tenant }>(`/admin/tenants/${uuid}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  async deleteTenant(uuid: string): Promise<{ success: boolean; message: string }> {
    return this.request(`/admin/tenants/${uuid}`, {
      method: 'DELETE',
    });
  }

  // ============================================================================
  // Users
  // ============================================================================

  async listUsers(options?: {
    tenantId?: string;
    limit?: number;
    offset?: number;
  }): Promise<ListUsersResponse> {
    const params = new URLSearchParams();
    if (options?.tenantId) params.set('tenantId', options.tenantId);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));

    const query = params.toString();
    return this.request<ListUsersResponse>(`/admin/users${query ? `?${query}` : ''}`);
  }

  async getUser(uuid: string): Promise<{ success: boolean; user: User }> {
    return this.request(`/admin/users/${uuid}`);
  }

  async createUser(input: CreateUserInput): Promise<{ success: boolean; user: User }> {
    return this.request<{ success: boolean; user: User }>('/admin/users', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async updateUser(uuid: string, input: UpdateUserInput): Promise<{ success: boolean; user: User }> {
    return this.request<{ success: boolean; user: User }>(`/admin/users/${uuid}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  async deleteUser(uuid: string): Promise<{ success: boolean; message: string }> {
    return this.request(`/admin/users/${uuid}`, {
      method: 'DELETE',
    });
  }

  // ============================================================================
  // API Keys
  // ============================================================================

  async listKeys(options?: {
    tenantId?: string;
    userId?: string;
    isActive?: boolean;
    includeExpired?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<ListApiKeysResponse> {
    const params = new URLSearchParams();
    if (options?.tenantId) params.set('tenantId', options.tenantId);
    if (options?.userId) params.set('userId', options.userId);
    if (options?.isActive !== undefined) params.set('isActive', String(options.isActive));
    if (options?.includeExpired) params.set('includeExpired', 'true');
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));

    const query = params.toString();
    return this.request<ListApiKeysResponse>(`/admin/keys${query ? `?${query}` : ''}`);
  }

  async getKey(uuid: string): Promise<{ success: boolean; key: ApiKey }> {
    return this.request(`/admin/keys/${uuid}`);
  }

  async createKey(input: CreateApiKeyInput): Promise<CreateApiKeyResponse> {
    return this.request<CreateApiKeyResponse>('/admin/keys', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async revokeKey(uuid: string): Promise<{ success: boolean; message: string }> {
    return this.request(`/admin/keys/${uuid}`, {
      method: 'DELETE',
    });
  }

  // ============================================================================
  // Audit Logs
  // ============================================================================

  async listAuditLogs(options?: {
    keyUuid?: string;
    action?: string;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
  }): Promise<ListAuditLogsResponse> {
    const params = new URLSearchParams();
    if (options?.keyUuid) params.set('keyUuid', options.keyUuid);
    if (options?.action) params.set('action', options.action);
    if (options?.from) params.set('from', options.from);
    if (options?.to) params.set('to', options.to);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));

    const query = params.toString();
    return this.request<ListAuditLogsResponse>(`/admin/audit-logs${query ? `?${query}` : ''}`);
  }

  // ============================================================================
  // Metrics
  // ============================================================================

  async listMetrics(options?: {
    functionName?: string;
    tenantId?: string;
    apiKeyUuid?: string;
    from?: string;
    to?: string;
    limit?: number;
  }): Promise<ListMetricsResponse> {
    const params = new URLSearchParams();
    if (options?.functionName) params.set('functionName', options.functionName);
    if (options?.tenantId) params.set('tenantId', options.tenantId);
    if (options?.apiKeyUuid) params.set('apiKeyUuid', options.apiKeyUuid);
    if (options?.from) params.set('from', options.from);
    if (options?.to) params.set('to', options.to);
    if (options?.limit) params.set('limit', String(options.limit));

    const query = params.toString();
    return this.request<ListMetricsResponse>(`/admin/metrics${query ? `?${query}` : ''}`);
  }

  async getSlowestFunctions(options?: {
    from?: string;
    to?: string;
    limit?: number;
  }): Promise<{ success: boolean; functions: SlowFunction[] }> {
    const params = new URLSearchParams();
    if (options?.from) params.set('from', options.from);
    if (options?.to) params.set('to', options.to);
    if (options?.limit) params.set('limit', String(options.limit));

    const query = params.toString();
    return this.request(`/admin/metrics/slowest${query ? `?${query}` : ''}`);
  }

  async getMostCalledFunctions(options?: {
    from?: string;
    to?: string;
    limit?: number;
  }): Promise<{ success: boolean; functions: MostCalledFunction[] }> {
    const params = new URLSearchParams();
    if (options?.from) params.set('from', options.from);
    if (options?.to) params.set('to', options.to);
    if (options?.limit) params.set('limit', String(options.limit));

    const query = params.toString();
    return this.request(`/admin/metrics/most-called${query ? `?${query}` : ''}`);
  }

  async getCostSummary(options?: {
    from?: string;
    to?: string;
    tenantId?: string;
  }): Promise<{ success: boolean; summary: CostSummary }> {
    const params = new URLSearchParams();
    if (options?.from) params.set('from', options.from);
    if (options?.to) params.set('to', options.to);
    if (options?.tenantId) params.set('tenantId', options.tenantId);

    const query = params.toString();
    return this.request(`/admin/metrics/costs${query ? `?${query}` : ''}`);
  }

  async getMetricsTrend(options: {
    functionName: string;
    from?: string;
    to?: string;
  }): Promise<{ success: boolean; trend: MetricsTrend[] }> {
    const params = new URLSearchParams();
    params.set('functionName', options.functionName);
    if (options.from) params.set('from', options.from);
    if (options.to) params.set('to', options.to);

    return this.request(`/admin/metrics/trend?${params.toString()}`);
  }

  // ============================================================================
  // Health Check
  // ============================================================================

  async healthCheck(): Promise<{ status: string }> {
    const response = await fetch(`${this.baseUrl.replace('/api', '')}/health`);
    return response.json();
  }

  // ============================================================================
  // Stats & Monitoring
  // ============================================================================

  async getMemoryStatsSummary(tenantId?: string): Promise<{ success: boolean; summary: MemoryStatsSummary }> {
    const params = new URLSearchParams();
    if (tenantId) params.set('tenantId', tenantId);
    const query = params.toString();
    return this.request(`/admin/stats/memory${query ? `?${query}` : ''}`);
  }

  async getSystemHealthSummary(): Promise<{ success: boolean; summary: SystemHealthSummary }> {
    return this.request('/admin/stats/health');
  }

  async checkSystemHealth(): Promise<{ success: boolean; health: SystemHealthSnapshot }> {
    return this.request('/admin/stats/health/check', { method: 'POST' });
  }

  async getErrorSummary(): Promise<{ success: boolean; summary: ErrorSummary }> {
    return this.request('/admin/stats/errors');
  }

  async getErrorLogs(options?: {
    errorType?: string;
    functionName?: string;
    tenantId?: string;
    resolved?: boolean;
    limit?: number;
  }): Promise<{ success: boolean; logs: ErrorLog[]; count: number }> {
    const params = new URLSearchParams();
    if (options?.errorType) params.set('errorType', options.errorType);
    if (options?.functionName) params.set('functionName', options.functionName);
    if (options?.tenantId) params.set('tenantId', options.tenantId);
    if (options?.resolved !== undefined) params.set('resolved', String(options.resolved));
    if (options?.limit) params.set('limit', String(options.limit));
    const query = params.toString();
    return this.request(`/admin/stats/errors/logs${query ? `?${query}` : ''}`);
  }

  async resolveError(uuid: string, resolution?: string): Promise<{ success: boolean; message: string }> {
    return this.request(`/admin/stats/errors/${uuid}/resolve`, {
      method: 'POST',
      body: resolution ? JSON.stringify({ resolution }) : undefined,
    });
  }

  async getAlertSummary(): Promise<{ success: boolean; summary: AlertSummary }> {
    return this.request('/admin/stats/alerts');
  }

  async getAlerts(options?: {
    alertType?: string;
    severity?: string;
    acknowledged?: boolean;
    resolved?: boolean;
    limit?: number;
  }): Promise<{ success: boolean; alerts: Alert[]; count: number }> {
    const params = new URLSearchParams();
    if (options?.alertType) params.set('alertType', options.alertType);
    if (options?.severity) params.set('severity', options.severity);
    if (options?.acknowledged !== undefined) params.set('acknowledged', String(options.acknowledged));
    if (options?.resolved !== undefined) params.set('resolved', String(options.resolved));
    if (options?.limit) params.set('limit', String(options.limit));
    const query = params.toString();
    return this.request(`/admin/stats/alerts/list${query ? `?${query}` : ''}`);
  }

  async acknowledgeAlert(uuid: string, acknowledgedBy?: string): Promise<{ success: boolean; message: string }> {
    return this.request(`/admin/stats/alerts/${uuid}/acknowledge`, {
      method: 'POST',
      body: acknowledgedBy ? JSON.stringify({ acknowledgedBy }) : undefined,
    });
  }

  async resolveAlert(uuid: string): Promise<{ success: boolean; message: string }> {
    return this.request(`/admin/stats/alerts/${uuid}/resolve`, { method: 'POST' });
  }

  async collectStats(tenantId?: string): Promise<{ success: boolean; stats: Record<string, unknown> }> {
    const params = new URLSearchParams();
    if (tenantId) params.set('tenantId', tenantId);
    const query = params.toString();
    return this.request(`/admin/stats/collect${query ? `?${query}` : ''}`, { method: 'POST' });
  }

  async detectProblems(): Promise<{
    success: boolean;
    alertsCreated: number;
    alerts: Array<{ type: string; severity: string; message: string }>;
  }> {
    return this.request('/admin/stats/detect-problems', { method: 'POST' });
  }
}

// Singleton instance - uses VITE_API_URL env var in production, /api proxy in development
const apiUrl = import.meta.env.VITE_API_URL ?? '/api';
export const apiClient = new AdminApiClient(apiUrl);

export default apiClient;
